import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Nav from "@/components/landing/Nav";
import Footer from "@/components/landing/Footer";
import WhatsAppSticky from "@/components/landing/WhatsAppSticky";
import LeadDialog from "@/components/landing/LeadDialog";
import Reveal from "@/components/landing/Reveal";
import ProductCard from "@/components/products/ProductCard";
import { fetchLandingPage, fetchProducts } from "@/lib/api";
import { LANDING_FAQS } from "@/data/landingFaqs";
import { assetUrl as buildAssetUrl } from "@/lib/assetUrl";

const assetUrl = (u) => (!u ? "" : u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

const setMeta = (name, content, attr = "name") => {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute("content", content);
};
const setCanonical = (href) => {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", "canonical"); document.head.appendChild(el); }
  el.setAttribute("href", href);
};
const setJsonLd = (id, data) => {
  let el = document.getElementById(id);
  if (!el) { el = document.createElement("script"); el.type = "application/ld+json"; el.id = id; document.head.appendChild(el); }
  el.textContent = JSON.stringify(data);
};

const WHY_CHOOSE = [
  { heading: "Thoughtfully Curated Premium Gifts", body: "Every hamper is designed around the recipient — with premium sourcing, considered packaging and a story worth telling." },
  { heading: "Custom Branding & Personalisation", body: "Add your company logo, colours and a personal message to every gift. Bespoke sleeves, greeting cards and branded packaging on request." },
  { heading: "Reliable Pan-India Delivery", body: "From a single office to hundreds of employee homes across India — delivered on schedule with 99.97% on-time performance." },
  { heading: "Sustainable Gifting with Purpose", body: "Every eligible hamper plants a tree in your company's name through our verified reforestation partner, Grow-Trees." },
];

export default function LandingSeoPage() {
  const { slug } = useParams();
  const [lp, setLp] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const [dialog, setDialog] = useState({ open: false, type: "bulk" });
  const openMeeting = useCallback(() => setDialog({ open: true, type: "meeting" }), []);
  const openBulk = useCallback(() => setDialog({ open: true, type: "bulk" }), []);
  const closeDialog = useCallback(() => setDialog((d) => ({ ...d, open: false })), []);
  const handlers = useMemo(() => ({ openMeeting, openBulk }), [openMeeting, openBulk]);

  useEffect(() => {
    let alive = true;
    setLoading(true); setNotFound(false);
    fetchLandingPage(slug)
      .then((page) => {
        if (!alive) return;
        setLp(page); setLoading(false);
        return fetchProducts();
      })
      .then((list) => {
        if (!alive || !list) return;
        setProducts(list);
      })
      .catch(() => { if (alive) { setNotFound(true); setLoading(false); } });
    window.scrollTo(0, 0);
    return () => { alive = false; };
  }, [slug]);

  const selectedProducts = useMemo(() => {
    if (!lp || !products.length) return [];
    const bySlug = new Map(products.map((p) => [p.slug, p]));
    return (lp.product_slugs || []).map((s) => bySlug.get(s)).filter(Boolean);
  }, [lp, products]);

  useEffect(() => {
    if (!lp) return;
    const title = (lp.seo_title && lp.seo_title.trim()) || `${lp.title} | Utsah`;
    const description = (lp.meta_description && lp.meta_description.trim()) || (lp.intro ? lp.intro.slice(0, 200) : "");
    const url = window.location.href;
    const image = assetUrl(lp.og_image) || assetUrl(lp.hero_image) || assetUrl(selectedProducts[0]?.images?.[0]);
    document.title = title;
    document.querySelectorAll('meta[name="description"]').forEach((el) => el.setAttribute("content", description));
    setMeta("og:type", "website", "property");
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    if (image) setMeta("og:image", image, "property");
    setMeta("og:url", url, "property");
    setCanonical(url);

    // Breadcrumb JSON-LD
    const origin = window.location.origin;
    setJsonLd("landing-breadcrumb-jsonld", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": `${origin}/` },
        { "@type": "ListItem", "position": 2, "name": "Collections", "item": `${origin}/diwali-collection` },
        { "@type": "ListItem", "position": 3, "name": lp.title, "item": url },
      ],
    });

    // CollectionPage JSON-LD
    setJsonLd("landing-jsonld", {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": lp.title,
      "description": (lp.meta_description || lp.intro || "").slice(0, 400),
      "url": url,
      "image": image || undefined,
      "hasPart": selectedProducts.slice(0, 20).map((p) => ({
        "@type": "Product",
        "name": p.name,
        "url": `${origin}/diwali-collection/${p.slug}`,
        "image": assetUrl(p.images?.[0]) || undefined,
        "offers": { "@type": "Offer", "price": p.price, "priceCurrency": "INR" },
      })),
    });
  }, [lp, selectedProducts]);

  if (loading) {
    return (
      <div className="utsah-app">
        <Nav onBookMeeting={openMeeting} />
        <div className="section" style={{ minHeight: "50vh", display: "grid", placeItems: "center", opacity: 0.6, fontStyle: "italic" }}>Loading…</div>
        <Footer handlers={handlers} />
      </div>
    );
  }
  if (notFound || !lp) return <Navigate to="/diwali-collection" replace />;

  // Hero banner image: use the landing page's own CMS `hero_image` when set,
  // otherwise fall back to the first selected product's primary image. Both
  // sources are existing CMS fields — no new fields are introduced.
  const heroImg =
    assetUrl(lp.hero_image) ||
    assetUrl(selectedProducts[0]?.images?.[0]) ||
    "";

  return (
    <div className="utsah-app">
      <Nav onBookMeeting={openMeeting} />

      {/* HERO BANNER — always rendered, with edge-to-edge CTA */}
      <section className="landing-hero-image" data-testid="landing-hero-image">
        {heroImg ? (
          <picture>
            <source
              media="(max-width: 720px)"
              srcSet={buildAssetUrl(lp.hero_image || (selectedProducts[0]?.images?.[0] ?? ""), 900)}
            />
            <img
              src={buildAssetUrl(lp.hero_image || (selectedProducts[0]?.images?.[0] ?? ""), 1920)}
              alt={lp.hero_title || lp.title}
              fetchpriority="high"
              decoding="async"
            />
          </picture>
        ) : null}
        <button
          type="button"
          className="landing-hero-cta"
          onClick={openMeeting}
          data-testid="landing-hero-cta"
        >
          Speak to a Gift Advisor
          <svg width="16" height="12" viewBox="0 0 18 12" fill="none" aria-hidden>
            <path d="M1 6h15M11 1l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </section>

      {/* BREADCRUMB + HEADLINE — intro paragraph is retained in CMS/SEO meta but
          intentionally not rendered on the visible page (see brief item 1). */}
      <section className="section landing-headline" data-testid="landing-headline" style={{ paddingTop: 32 }}>
        <div className="section-inner landing-headline-inner">
          <nav className="landing-breadcrumb" aria-label="Breadcrumb" data-testid="landing-breadcrumb">
            <Link to="/">Home</Link>
            <span aria-hidden> › </span>
            <Link to="/diwali-collection">Collections</Link>
            <span aria-hidden> › </span>
            <span className="landing-breadcrumb-current">{lp.title}</span>
          </nav>
          <Reveal as="h1" className="section-headline landing-h1" data-testid="landing-title">{lp.hero_title || lp.title}</Reveal>
        </div>
      </section>

      {/* PRODUCTS — no eyebrow / no heading, straight into grid */}
      {selectedProducts.length > 0 && (
        <section className="section landing-products" data-testid="landing-products" style={{ paddingTop: 40 }}>
          <div className="section-inner">
            <div className="dc-grid" data-testid="landing-products-grid">
              {selectedProducts.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* WHY CHOOSE UTSAH */}
      <section className="section landing-why" data-testid="landing-why">
        <div className="section-inner">
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <Reveal className="eyebrow" style={{ display: "inline-flex", marginLeft: "auto", marginRight: "auto" }}>Why Choose Utsah</Reveal>
            <Reveal as="h2" delay={1} className="section-headline">
              Gifting done <em>right.</em>
            </Reveal>
          </div>
          <div className="landing-why-grid">
            {WHY_CHOOSE.map((r, i) => (
              <Reveal key={r.heading} delay={i > 3 ? 3 : i} className="landing-why-card" data-testid={`landing-why-${i}`}>
                <div className="landing-why-num">{String(i + 1).padStart(2, "0")}</div>
                <div className="landing-why-title">{r.heading}</div>
                <div className="landing-why-body">{r.body}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SHARED FAQs (same content as homepage) */}
      <section className="section faq" data-testid="landing-faqs">
        <div className="section-inner">
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <Reveal className="eyebrow" style={{ display: "inline-flex", marginLeft: "auto", marginRight: "auto" }}>Frequently Asked Questions</Reveal>
            <Reveal as="h2" delay={1} className="section-headline">Everything you need <em>to know.</em></Reveal>
          </div>
          <div className="home-faq-grid">
            {LANDING_FAQS.map(([q, a], i) => {
              const open = openFaq === i;
              return (
                <div key={q} className={`faq-item ${open ? "open" : ""}`.trim()} data-testid={`landing-faq-${i}`}>
                  <button className="faq-q" onClick={() => setOpenFaq(open ? null : i)}
                    style={{ width: "100%", background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}>
                    <h3 className="faq-q-text">{q}</h3>
                    <div className="faq-q-icon">+</div>
                  </button>
                  {open && <div className="faq-a" dangerouslySetInnerHTML={{ __html: a }} />}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ENQUIRY CTA */}
      <section className="section s6" data-testid="landing-cta-section">
        <div className="section-inner s6-inner">
          <Reveal className="eyebrow cream cta-eyebrow" style={{ marginLeft: "auto", marginRight: "auto" }}>Ready to Gift Thoughtfully?</Reveal>
          <Reveal as="h2" delay={1} className="cta-headline">
            Talk to our<br /><em>gifting specialists.</em>
          </Reveal>
          <Reveal as="p" delay={2} className="cta-body">
            Share your requirements and we'll get back with a curated proposal, pricing and samples within one business day.
          </Reveal>
          <Reveal delay={3}>
            <button className="btn-primary" onClick={openBulk} data-testid="landing-enquire-btn">Get a Quote</button>
          </Reveal>
        </div>
      </section>

      <Footer handlers={handlers} />
      <WhatsAppSticky />
      <LeadDialog open={dialog.open} type={dialog.type} source={`Landing · ${lp.title} (/collections/${lp.slug})`} onOpenChange={(v) => (v ? null : closeDialog())} />
      <Toaster position="bottom-center" richColors theme="light" />
    </div>
  );
}
